import React, { useState } from 'react';
import '../styles/Contact.css';
import useMetaTags from '../hooks/useMetaTags';
import { Link } from 'react-router-dom';

const Contact = () => {
  useMetaTags({
    title: 'Contact Me - Sam Penny',
    description: 'Get in touch with Sam Penny to discuss SEO conferences, guest posting opportunities, or consulting/coaching.'
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    website: '', // honeypot field
    notLinkSeller: false // new field for the checkbox
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if honeypot field is filled (bot detected)
    if (formData.website) {
      console.log('Honeypot detected potential spam');
      setSubmitStatus('success'); // Pretend success to fool bots
      return;
    }

    // Check if user confirmed they're not a link seller
    if (!formData.notLinkSeller) {
      setSubmitStatus('link-seller');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    
    // Determine if we're in development or production
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const apiUrl = isDevelopment 
      ? 'http://localhost:3001/api/send-email' 
      : '/api/send-email';  // Use relative path in production
    
    try {
      console.log('Submitting form to:', apiUrl);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
        mode: 'cors'
      });

      let data;
      try {
        data = await response.json();
        console.log('Server response:', data);
      } catch (jsonError) {
        console.error('Error parsing JSON response:', jsonError);
        throw new Error('Invalid server response');
      }

      if (response.ok && (data.status === 'success' || data.message === 'Email sent successfully')) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '', website: '', notLinkSeller: false });
      } else {
        console.error('Server error:', data);
        throw new Error(data.message || data.error || data.details || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      if (!navigator.onLine) {
        setSubmitStatus('offline');
      } else if (error.message.includes('Invalid server response')) {
        setSubmitStatus('server');
      } else if (error.message.includes('Failed to fetch')) {
        setSubmitStatus('network');
      } else {
        setSubmitStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <h1>Well hello, stranger</h1>
      <div className="contact-container">
        <div className="contact-info">
          <h2>Let's talk</h2>
          <p>Get in touch using the address below if you'd like to discuss SEO conferences, guest posting opportunities, or consulting/coaching.</p>
          <p>I keep my books open for a very small number of clients at any time, so reach out to see if I have space.</p>
          
          <div className="info-section">
            <a 
              href="mailto:&#115;&#97;&#109;&#64;&#115;&#97;&#109;&#112;&#101;&#110;&#110;&#121;&#46;&#105;&#111;" 
              className="email-link"
            >
              &#115;&#97;&#109;&#64;&#115;&#97;&#109;&#112;&#101;&#110;&#110;&#121;&#46;&#105;&#111;
            </a>
          </div>

          <div className="divider"></div>

          <div className="info-section location">
            <p>SamPenny.io</p>
            <p>Melbourne, Australia</p>
          </div>

          <div className="social-links">
            <a href="https://www.linkedin.com/in/sampenny/" target="_blank" rel="noopener noreferrer" className="social-link">
              <img src="/images/linkedin-dark.svg" alt="LinkedIn" loading="lazy" />
            </a>
            <a href="https://x.com/sampennyseo" target="_blank" rel="noopener noreferrer" className="social-link">
              <img src="/images/x-dark.svg" alt="X (formerly Twitter)" loading="lazy" />
            </a>
          </div>
        </div>
        
        <div className="contact-form">
          <h2>Send me a message</h2>
          <form onSubmit={handleSubmit}>
            {/* Honeypot field - hidden from real users */}
            <div style={{ opacity: 0, position: 'absolute', top: 0, left: 0, height: 0, width: 0, zIndex: -1 }}>
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="5"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="notLinkSeller"
                  checked={formData.notLinkSeller}
                  onChange={handleChange}
                  required
                />
                <span>I confirm I am not a link seller. All emails from link sellers will be ignored.</span>
              </label>
            </div>
            
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
            
            {submitStatus === 'success' && (
              <p className="success-message">Message sent successfully!</p>
            )}
            {submitStatus === 'error' && (
              <p className="error-message">Failed to send message. Please try again.</p>
            )}
            {submitStatus === 'offline' && (
              <p className="error-message">You appear to be offline. Please check your internet connection and try again.</p>
            )}
            {submitStatus === 'network' && (
              <p className="error-message">Network error. Please check your connection and try again.</p>
            )}
            {submitStatus === 'server' && (
              <p className="error-message">Server error. Please try again later.</p>
            )}
            {submitStatus === 'link-seller' && (
              <p className="error-message">Please check this box if you want to proceed.</p>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default Contact; 